import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "dark" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

const VARIANTS: Record<Variant, string> = {
  primary:   "bg-gold text-ink hover:bg-gold-dark",
  dark:      "bg-ink text-white hover:bg-black",
  secondary: "border border-line-strong bg-white text-ink-soft hover:bg-cream",
  ghost:     "text-gold-ink hover:bg-cream",
  danger:    "bg-red-600 text-white hover:bg-red-700",
};

const SIZES: Record<Size, string> = {
  sm: "px-3 py-1.5 text-xs rounded-lg",
  md: "px-4 py-2.5 text-sm rounded-xl",
  lg: "px-6 py-3 text-sm rounded-xl",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Remix icon class, e.g. "ri-add-line" — rendered before the label. */
  icon?: string;
  /** Use the Tajawal font (Arabic UI). */
  arFont?: boolean;
  children?: ReactNode;
}

/**
 * Shared button. Prefer this over hand-styled <button>s so colours,
 * sizes, focus and disabled states stay consistent across the app.
 */
export default function Button({
  variant = "primary", size = "md", icon, arFont, className = "", type = "button", children, ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center gap-2 font-semibold cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${VARIANTS[variant]} ${SIZES[size]} ${arFont ? "font-tajawal" : ""} ${className}`}
      {...rest}
    >
      {icon && <i className={icon} aria-hidden="true"></i>}
      {children}
    </button>
  );
}
